// Shared constants for vim-dojo

/**
 * Lessons accessible without authentication (public lessons).
 * Note: This is used for server-side validation. Client derives from manifest.
 */
export const PUBLIC_LESSONS = [
  '1-hjkl-basics',
  '27-lazy-intro',
  '31-lazyvim-sidebar',
];

/**
 * Free lessons — accessible to any authenticated user without payment.
 * Includes PUBLIC_LESSONS (which don't even need auth).
 */
export const FREE_LESSONS = [
  '1-hjkl-basics',
  '2-substitute-command',
  '3-macros',
  '4-word-motions',
  '5-WORD-motions',
  '6-find-character',
  '7-line-extremes',
  '27-lazy-intro',
  '28-lazy-specs',
  '29-neovim-themes',
  '30-save-file',
  '31-lazyvim-sidebar',
  '32-lazyvim-ui-toggles',
  '33-lazyvim-windows',
  '34-lazyvim-project-search',
  '35-lazyvim-diagnostics',
  '36-lazyvim-rename',
];

/**
 * Check if a lesson is public (does not require authentication).
 */
export function isPublicLesson(lessonId: string): boolean {
  return PUBLIC_LESSONS.includes(lessonId);
}

/**
 * Check if a lesson is free (accessible without payment).
 */
export function isFreeLesson(lessonId: string): boolean {
  return FREE_LESSONS.includes(lessonId);
}

/**
 * Lessons that run in the Neovim sandbox image (Row 2 — hand-rolled
 * lazy.nvim config). Still active alongside LAZYVIM_LESSONS below; see
 * docs/steps/00-lazyvim-distro-curriculum.md for the plan to eventually
 * delist these in favor of the LazyVim track.
 */
export const NVIM_LESSONS = [
  '27-lazy-intro',
  '28-lazy-specs',
  '29-neovim-themes',
];

/**
 * Lessons that run in the LazyVim sandbox image (Row 2 — LazyVim distro).
 * See docs/steps/00-lazyvim-distro-curriculum.md and
 * docs/steps/01-lazyvim-sandbox-image.md.
 */
export const LAZYVIM_LESSONS: string[] = [
  '31-lazyvim-sidebar',
  '32-lazyvim-ui-toggles',
  '33-lazyvim-windows',
  '34-lazyvim-project-search',
  '35-lazyvim-diagnostics',
  '36-lazyvim-rename',
];

export type LessonEngine = 'vim' | 'nvim' | 'lazyvim';

/**
 * Returns which sandbox engine a lesson should run under.
 */
export function getLessonEngine(lessonId: string): LessonEngine {
  if (LAZYVIM_LESSONS.includes(lessonId)) return 'lazyvim';
  if (NVIM_LESSONS.includes(lessonId)) return 'nvim';
  return 'vim';
}

/**
 * Lessons that ship a drill pack (lessons/<id>/drills.json).
 * Drill mode is offered as a post-lesson CTA only for these IDs.
 */
export const DRILL_LESSONS = [
  '4-word-motions',
  '5-WORD-motions',
  '6-find-character',
  '7-line-extremes',
  '8-vertical-jumps',
  '9-search-navigation',
  '10-file-extremes',
];

export function hasDrills(lessonId: string): boolean {
  return DRILL_LESSONS.includes(lessonId);
}
